/**
 * OverlayWindowRenderer — abstract base for DI and Burst overlay windows.
 *
 * Shared infrastructure: container setup, show/hide lifecycle, timer bar,
 * destroy. Subclasses implement the per-overlay visual logic (ring, arrow,
 * trajectory arc, ghost, vignette, prompt text, update loop).
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, Z } from '../constants';
import type { GameState } from '../types';
import type { ParticleSystem } from './particles';
import { getUrgencyColor, getUrgencyPulse } from './urgency';

export abstract class OverlayWindowRenderer {
    protected worldContainer: Container;
    protected uiContainer: Container;
    protected overlayContainer: Container;     // world-space
    protected overlayUIContainer: Container;   // screen-space

    // World-space elements
    protected ringGfx: Graphics;
    protected arrowGfx: Graphics;
    protected arcGfx: Graphics;
    protected ghostGfx: Graphics;

    // UI elements
    protected vignetteGfx: Graphics;
    protected timerBarBg: Graphics;
    protected timerBarFill: Graphics;
    protected promptText: Text;

    protected active: boolean = false;
    protected particles: ParticleSystem;

    constructor(
        worldContainer: Container,
        uiContainer: Container,
        particles: ParticleSystem,
        promptFontSize: number,
    ) {
        this.worldContainer = worldContainer;
        this.uiContainer = uiContainer;
        this.particles = particles;

        // World-space container
        this.overlayContainer = new Container();
        this.overlayContainer.zIndex = Z.DI_OVERLAY;
        this.overlayContainer.visible = false;
        this.worldContainer.addChild(this.overlayContainer);

        this.ringGfx = new Graphics();
        this.overlayContainer.addChild(this.ringGfx);

        this.arrowGfx = new Graphics();
        this.overlayContainer.addChild(this.arrowGfx);

        this.arcGfx = new Graphics();
        this.overlayContainer.addChild(this.arcGfx);

        this.ghostGfx = new Graphics();
        this.overlayContainer.addChild(this.ghostGfx);

        // UI container
        this.overlayUIContainer = new Container();
        this.overlayUIContainer.zIndex = Z.DI_OVERLAY;
        this.overlayUIContainer.visible = false;
        this.uiContainer.addChild(this.overlayUIContainer);

        this.vignetteGfx = new Graphics();
        this.overlayUIContainer.addChild(this.vignetteGfx);

        this.timerBarBg = new Graphics();
        this.overlayUIContainer.addChild(this.timerBarBg);

        this.timerBarFill = new Graphics();
        this.overlayUIContainer.addChild(this.timerBarFill);

        this.promptText = new Text({
            text: 'HOLD DIRECTION',
            style: new TextStyle({
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: promptFontSize,
                fill: 0xffffff,
                stroke: { color: 0x000000, width: 3 },
                align: 'center',
                letterSpacing: 2,
            }),
        });
        this.promptText.anchor.set(0.5, 0.5);
        this.overlayUIContainer.addChild(this.promptText);
    }

    show(): void {
        this.active = true;
        this.overlayContainer.visible = true;
        this.overlayUIContainer.visible = true;
        this.drawVignette();
    }

    hide(): void {
        this.active = false;
        this.overlayContainer.visible = false;
        this.overlayUIContainer.visible = false;
        this.ringGfx.clear();
        this.arrowGfx.clear();
        this.arcGfx.clear();
        this.ghostGfx.clear();
        this.ghostGfx.position.set(0, 0);
        this.ghostGfx.alpha = 1;
        this.timerBarBg.clear();
        this.timerBarFill.clear();
    }

    /**
     * Draw timer bar with 3-color urgency pattern.
     * @param timer Current timer value (seconds remaining)
     * @param duration Total window duration (seconds)
     * @param barWidth Bar pixel width
     * @param barHeight Bar pixel height
     * @param bgColor Background color
     */
    protected drawTimerBar(
        timer: number,
        duration: number,
        barWidth: number,
        barHeight: number,
        bgColor: number,
    ): void {
        this.timerBarBg.clear();
        this.timerBarFill.clear();

        const x = (WORLD.CANVAS_WIDTH - barWidth) / 2;
        const y = 30;

        this.timerBarBg.roundRect(x, y, barWidth, barHeight, 4);
        this.timerBarBg.fill({ color: bgColor, alpha: 0.8 });

        const frac = Math.max(0, timer / duration);
        const fillColor = getUrgencyColor(frac);
        const pulse = getUrgencyPulse(frac, Date.now() * 0.001);

        if (frac > 0) {
            this.timerBarFill.roundRect(x, y, barWidth * frac, barHeight, 4);
            this.timerBarFill.fill({ color: fillColor, alpha: 0.9 * pulse });
        }
    }

    destroy(): void {
        this.overlayContainer.destroy({ children: true });
        this.overlayUIContainer.destroy({ children: true });
    }

    abstract update(state: GameState, dt: number): void;
    protected abstract drawVignette(): void;
}
